import React, { useState, useCallback } from 'react';
import { CheckCircle, Circle, Trophy, Target, BookOpen, Compass, Ruler, Brain, Calculator, Search } from 'lucide-react';

interface Competency {
  id: string;
  name: string;
  description: string;
  weight: number;
  activities: string[];
}

interface EvaluationDashboardResource {
  type: 'evaluation_dashboard';
  name: string;
  title: string;
  description: string;
  competencies: Competency[];
  showProgress: boolean;
  allowRetry: boolean;
}

interface EvaluationDashboardComponentProps {
  resource: EvaluationDashboardResource;
  onAction?: (action: string, data: any) => void;
}

export const EvaluationDashboardComponent: React.FC<EvaluationDashboardComponentProps> = ({
  resource,
  onAction
}) => {
  const [competencyProgress, setCompetencyProgress] = useState<Record<string, number>>({});
  const [completedActivities, setCompletedActivities] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'completed' | 'in_progress' | 'not_started'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'progress' | 'weight'>('progress');
  const [activeCompetency, setActiveCompetency] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  const updateCompetencyProgress = useCallback((competencyId: string, progress: number) => {
    setCompetencyProgress(prev => ({
      ...prev,
      [competencyId]: Math.min(100, Math.max(0, progress))
    }));
  }, []);



  // Fonction pour démarrer une compétence
  const startCompetency = useCallback((competencyId: string) => {
    setActiveCompetency(competencyId);
    
    // Simuler le démarrage d'une activité
    const competency = resource.competencies.find(c => c.id === competencyId);
    if (competency && competency.activities.length > 0) {
      // Marquer la première activité comme en cours
      const firstActivity = competency.activities[0];
      const activityId = `${competencyId}_${firstActivity}`;
      setCompletedActivities(prev => new Set(prev).add(activityId));
      
      // Mettre à jour la progression
      const progress = (1 / competency.activities.length) * 100;
      updateCompetencyProgress(competencyId, progress);
    }

    if (onAction) {
      onAction('competency_started', { 
        competencyId, 
        competencyName: competency?.name,
        activities: competency?.activities.length || 0
      });
    }
  }, [resource.competencies, onAction, updateCompetencyProgress]);

  // Fonction pour continuer une compétence en cours
  const continueCompetency = useCallback((competencyId: string) => {
    console.log('🔄 continueCompetency appelé pour:', competencyId);
    
    setActiveCompetency(competencyId);
    
    const competency = resource.competencies.find(c => c.id === competencyId);
    if (competency) {
      console.log('📋 Compétence trouvée:', competency.name);
      console.log('📊 Activités actuelles complétées:', Array.from(completedActivities).filter(id => id.startsWith(competencyId)));
      
      // Trouver la prochaine activité non complétée
      const nextActivity = competency.activities.find(activity => 
        !completedActivities.has(`${competencyId}_${activity}`)
      );
      
      console.log('🎯 Prochaine activité:', nextActivity);
      
      if (nextActivity) {
        const activityId = `${competencyId}_${nextActivity}`;
        console.log('✅ Ajout de l\'activité:', activityId);
        
        // Mettre à jour les activités
        setCompletedActivities(prev => {
          const newCompleted = new Set(prev);
          newCompleted.add(activityId);
          console.log('📝 Nouvelles activités complétées:', Array.from(newCompleted).filter(id => id.startsWith(competencyId)));
          return newCompleted;
        });
        
        // Mettre à jour la progression
        setCompetencyProgress(prev => {
          const newCompleted = new Set(completedActivities);
          newCompleted.add(activityId);
          const completedCount = competency.activities.filter(activity => 
            newCompleted.has(`${competencyId}_${activity}`)
          ).length;
          const progress = (completedCount / competency.activities.length) * 100;
          console.log('📈 Nouvelle progression:', progress + '%');
          return {
            ...prev,
            [competencyId]: progress
          };
        });
      } else {
        console.log('⚠️ Aucune activité à continuer');
      }
    }

    if (onAction) {
      onAction('competency_continued', { 
        competencyId, 
        competencyName: competency?.name,
        progress: competencyProgress[competencyId] || 0
      });
    }
  }, [resource.competencies, onAction, completedActivities, competencyProgress]);

  // Fonction pour arrêter une compétence active
  const stopCompetency = useCallback((competencyId: string) => {
    setActiveCompetency(null);
    
    if (onAction) {
      onAction('competency_stopped', { 
        competencyId, 
        competencyName: resource.competencies.find(c => c.id === competencyId)?.name
      });
    }
  }, [resource.competencies, onAction]);

  const getCompetencyIcon = (competencyId: string) => {
    const iconMap: { [key: string]: React.ComponentType<any> } = {
      sorting: Target,
      spatial: Compass,
      geometry: BookOpen,
      measurement: Ruler,
      logic: Brain,
      calculation: Calculator
    };
    return iconMap[competencyId] || Circle;
  };



  // Filtrer et trier les compétences
  const filteredAndSortedCompetencies = resource.competencies
    .filter(competency => {
      const matchesSearch = competency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           competency.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) return false;
      
      const progress = competencyProgress[competency.id] || 0;
      switch (filterStatus) {
        case 'completed':
          return progress >= 80;
        case 'in_progress':
          return progress > 0 && progress < 80;
        case 'not_started':
          return progress === 0;
        default:
          return true;
      }
    })
    .sort((a, b) => {
      const progressA = competencyProgress[a.id] || 0;
      const progressB = competencyProgress[b.id] || 0;
      
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'weight':
          return b.weight - a.weight;
        case 'progress':
        default:
          return progressB - progressA;
      }
    });

  const totalProgress = resource.competencies.reduce((sum, competency) => {
    const progress = competencyProgress[competency.id] || 0;
    return sum + (progress * competency.weight / 100);
  }, 0);

  const completedCompetencies = resource.competencies.filter(competency => 
    (competencyProgress[competency.id] || 0) >= 80
  ).length;

  return (
    <div className="w-full p-4 bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header compact pour panel */}
      <div className="bg-white rounded-lg shadow-sm p-3 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{resource.title}</h2>
            <p className="text-gray-600 text-xs">{resource.description}</p>
          </div>
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-green-600 font-medium">Actif</span>
          </div>
        </div>
      </div>

      {/* Progression globale - Design compact pour panel */}
      <div className="bg-white rounded-lg shadow-sm p-3 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900">Progression Globale</h3>
          <div className="flex items-center space-x-2">
            <div className="text-right">
              <div className="text-lg font-bold text-blue-600">{Math.round(totalProgress)}%</div>
              <div className="text-xs text-gray-500">Complété</div>
            </div>
            <Trophy className="w-4 h-4 text-yellow-500" />
          </div>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
          <div 
            className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-700"
            style={{ width: `${totalProgress}%` }}
          />
        </div>
        
        <div className="flex justify-between text-xs text-gray-600">
          <span>{completedCompetencies}/{resource.competencies.length} compétences</span>
          <span>{Math.round(totalProgress)}% progression</span>
        </div>
      </div>

      {/* Barre de recherche et filtres - Compact pour panel */}
      <div className="bg-white rounded-lg shadow-sm p-3 mb-4">
        <div className="flex flex-col gap-3">
          {/* Recherche */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-7 pr-3 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          
          {/* Filtres compacts */}
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="all">Toutes</option>
              <option value="not_started">Non commencées</option>
              <option value="in_progress">En cours</option>
              <option value="completed">Complétées</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="progress">Progression</option>
              <option value="name">Nom</option>
              <option value="weight">Poids</option>
            </select>
          </div>
        </div>
        
        {/* Résultats du filtre */}
        <div className="mt-2 text-xs text-gray-600">
          {filteredAndSortedCompetencies.length} compétence(s)
        </div>
      </div>

      {/* Liste des compétences - Une colonne pour panel */}
      {filteredAndSortedCompetencies.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-4 text-center">
          <div className="text-gray-400 mb-2">
            <Search className="w-8 h-8 mx-auto" />
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">Aucune compétence trouvée</h3>
          <p className="text-xs text-gray-600 mb-3">
            Aucune compétence ne correspond à vos critères.
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setFilterStatus('all');
            }}
            className="px-3 py-1.5 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors"
          >
            Réinitialiser
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredAndSortedCompetencies.map((competency) => {
          const Icon = getCompetencyIcon(competency.id);
          const progress = competencyProgress[competency.id] || 0;
          const isCompleted = progress >= 80;
          const isInProgress = progress > 0 && progress < 80;
          
          return (
            <div 
              key={competency.id}
              className={`bg-white rounded-lg shadow-sm border p-3 transition-all duration-300 hover:shadow-md ${
                isCompleted 
                  ? 'border-green-300 bg-green-50' 
                  : isInProgress
                  ? 'border-blue-300 bg-blue-50'
                  : activeCompetency === competency.id
                  ? 'border-yellow-300 bg-yellow-50 ring-2 ring-yellow-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Header de la compétence - Compact */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <div className={`p-1.5 rounded ${
                    isCompleted 
                      ? 'bg-green-100' 
                      : isInProgress 
                      ? 'bg-blue-100' 
                      : activeCompetency === competency.id
                      ? 'bg-yellow-100'
                      : 'bg-gray-100'
                  }`}>
                    <Icon className={`w-4 h-4 ${
                      isCompleted 
                        ? 'text-green-600' 
                        : isInProgress 
                        ? 'text-blue-600' 
                        : activeCompetency === competency.id
                        ? 'text-yellow-600'
                        : 'text-gray-600'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 text-xs leading-tight truncate">{competency.name}</h4>
                    <p className="text-xs text-gray-500">{competency.weight}% • {Math.round(progress)}%</p>
                  </div>
                </div>
                {isCompleted && <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />}
              </div>
              
              {/* Barre de progression compacte */}
              <div className="mb-2">
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div 
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      isCompleted 
                        ? 'bg-green-500' 
                        : isInProgress 
                        ? 'bg-blue-500' 
                        : activeCompetency === competency.id
                        ? 'bg-yellow-500'
                        : 'bg-gray-400'
                    }`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
              
              {/* Liste complète des activités */}
              <div className="mb-2">
                <div className="text-xs text-gray-600 mb-1">
                  {competency.activities.filter((_, index) => 
                    completedActivities.has(`${competency.id}_${competency.activities[index]}`)
                  ).length}/{competency.activities.length} activités
                </div>
                <div className="space-y-0.5 max-h-20 overflow-y-auto">
                  {competency.activities.map((activity, index) => {
                    const activityId = `${competency.id}_${activity}`;
                    const isActivityCompleted = completedActivities.has(activityId);
                    
                    return (
                      <div 
                        key={index}
                        className={`flex items-center space-x-1 text-xs ${
                          isActivityCompleted ? 'text-green-600' : 'text-gray-500'
                        }`}
                      >
                        {isActivityCompleted ? (
                          <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                        ) : (
                          <Circle className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        )}
                        <span className="truncate">{activity}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Bouton d'action compact */}
              <button
                onClick={() => {
                  console.log('🔄 continueCompetency appelé pour:', competency.id);
                  console.log('🔄 isCompleted:', isCompleted);
                  console.log('🔄 activeCompetency:', activeCompetency);
                  console.log('🔄 isInProgress:', isInProgress);
                  if (isCompleted) {
                    // Compétence déjà complétée
                    if (onAction) {
                      onAction('competency_viewed', { competencyId: competency.id });
                    }
                  } else if (isInProgress) {
                    // Continuer la compétence en cours
                    continueCompetency(competency.id);
                  } else if (activeCompetency === competency.id) {
                    // Arrêter la compétence active
                    stopCompetency(competency.id);
                  } else {
                    // Démarrer une nouvelle compétence
                    startCompetency(competency.id);
                  }
                }}
                className={`w-full px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                  isCompleted
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : isInProgress
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : activeCompetency === competency.id
                    ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {isCompleted 
                  ? '✓ Complété' 
                  : isInProgress 
                  ? '↻ Continuer' 
                  : activeCompetency === competency.id
                  ? '⏹ Arrêter'
                  : '▶ Commencer'
                }
              </button>
            </div>
          );
        })}
        </div>
      )}

      {/* Interface des résultats */}
      {showResults && (
        <div className="mt-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              <span>Résultats de l'Évaluation</span>
            </h3>
            <div className="text-sm text-gray-600">
              {new Date().toLocaleDateString('fr-FR')}
            </div>
          </div>

          {/* Score global */}
          <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Score Global</span>
              <span className="text-2xl font-bold text-green-600">{Math.round(totalProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-green-400 to-green-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${totalProgress}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Détail des compétences */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Détail par Compétence</h4>
            {resource.competencies.map(competency => {
              const progress = competencyProgress[competency.id] || 0;
              const isCompleted = progress >= 100;
              const isInProgress = progress > 0 && progress < 100;
              
              return (
                <div key={competency.id} className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {isCompleted ? (
                        <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      ) : isInProgress ? (
                        <div className="w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">↻</span>
                        </div>
                      ) : (
                        <div className="w-4 h-4 bg-gray-300 rounded-full" />
                      )}
                      <span className="text-sm font-medium text-gray-800">{competency.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-700">{Math.round(progress)}%</div>
                      <div className="text-xs text-gray-500">{competency.weight}% du total</div>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-500 ${
                        isCompleted ? 'bg-green-500' : isInProgress ? 'bg-yellow-500' : 'bg-gray-300'
                      }`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  
                  <div className="text-xs text-gray-600">
                    {competency.activities.filter((_, index) => 
                      completedActivities.has(`${competency.id}_${competency.activities[index]}`)
                    ).length}/{competency.activities.length} activités complétées
                  </div>
                </div>
              );
            })}
          </div>

          {/* Actions de résultats */}
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => {
                if (onAction) {
                  onAction('export_results', { 
                    totalProgress, 
                    completedCompetencies,
                    competencyProgress,
                    completedActivities: Array.from(completedActivities)
                  });
                }
              }}
              className="flex-1 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center justify-center space-x-1 text-xs"
            >
              <span>📊</span>
              <span>Exporter</span>
            </button>
            
            <button
              onClick={() => {
                if (onAction) {
                  onAction('print_results', { 
                    totalProgress, 
                    completedCompetencies,
                    competencyProgress
                  });
                }
              }}
              className="flex-1 px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors flex items-center justify-center space-x-1 text-xs"
            >
              <span>🖨️</span>
              <span>Imprimer</span>
            </button>
          </div>
        </div>
      )}

      {/* Actions globales - Compact pour panel */}
      <div className="mt-4 bg-white rounded-lg shadow-sm p-3">
        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowResults(!showResults);
                if (onAction) {
                  onAction('view_results', { totalProgress, completedCompetencies });
                }
              }}
              className="flex-1 px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors flex items-center justify-center space-x-1 text-xs"
            >
              <Trophy className="w-3 h-3" />
              <span>{showResults ? 'Masquer' : 'Résultats'}</span>
            </button>
            
            {resource.allowRetry && (
              <button
                onClick={() => {
                  setCompetencyProgress({});
                  setCompletedActivities(new Set());
                  if (onAction) {
                    onAction('retry_evaluation', {});
                  }
                }}
                className="flex-1 px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-xs"
              >
                Recommencer
              </button>
            )}
          </div>
          
          <div className="text-xs text-gray-600 text-center">
            <span className="font-medium">{completedCompetencies}</span>/{resource.competencies.length} compétences
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluationDashboardComponent;
